/**
 * 「{deliveryNote}作成」/delivery-notes/new（#266、docs/96 v0.2 の起こし方3＝見積なし）：{customer}と件名だけ聞く。{project}は一緒にできる（件名が名前）。
 *   ?group=<id> で開くと その{project}の{deliveryNote}（{customer}は{project}のまま＝聞かない）。?partner=<id> で{customer}を選んだ形で始める。
 *   見積から（起こし方1）は受注した見積の画面、{project}の画面のタブ（起こし方2）からもここへ来る
 *   1b：?kind=acceptance で「{acceptance}作成」（{customer}だけ／{project}から。POST /acceptances）
 *   #289（docs/101 §6）：{customer}から作るときは{project}の欄（components/ProjectPicker.tsx。既定は GET /groups/default）。件名は空でもよい（紙に出さない）
 */
import { ACCEPTANCE_MSG, DELIVERY_NOTE_MSG, displayNameOf, ongoingNameOf, type CreateWarning, type DeliveryNote, type GroupDefault, type GroupDetail, type GroupList, type Partner, type ProjectChoice } from "@genbaryoku/contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { api, ApiError, messageOf } from "../../api.js";
import { BackButton, useBackNav, usePageName } from "../../components/BackButton.jsx";
import { PartnerField } from "../../components/PartnerPickerDrawer.jsx";
import { ProjectPicker } from "../../components/ProjectPicker.jsx";
import { ErrorText, Field } from "../../components/ui.jsx";
import { useOngoing } from "../../lib/ongoing.js";
import { t, useLabels } from "../../labels.js";

export function DeliveryNoteNewPage() {
  const L = useLabels();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [sp] = useSearchParams();
  const groupId = sp.get("group") ?? "";
  const isAc = sp.get("kind") === "acceptance";
  const pageTitle = t(isAc ? ACCEPTANCE_MSG.create : DELIVERY_NOTE_MSG.newTitle);
  const [partnerId, setPartnerId] = useState(sp.get("partner") ?? "");
  /** 選んだ{customer}（#288 の取引先検索。?partner= で入ったときは名前を引く） */
  const [picked, setPicked] = useState<{ name: string; branch: string | null; contact: string | null } | null>(null);
  const [subject, setSubject] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | undefined>();
  usePageName(pageTitle);
  const back = useBackNav(groupId ? { to: `/groups/${groupId}?tab=dnotes`, label: L.project } : { to: isAc ? "/delivery-notes?tab=acceptance" : "/delivery-notes", label: t(DELIVERY_NOTE_MSG.list) }, !!partnerId || !!subject);
  const group = useQuery({ queryKey: ["group", groupId], queryFn: () => api<GroupDetail>("GET", `/groups/${groupId}`), enabled: !!groupId });
  const entry = useQuery({ queryKey: ["partner", sp.get("partner")], queryFn: () => api<Partner>("GET", `/partners/${sp.get("partner")}`), enabled: !groupId && !!sp.get("partner") });
  const shown = picked ?? (entry.data && entry.data.id === partnerId ? { name: entry.data.displayName, branch: null, contact: null } : null);
  // {project}の欄（#289）：{customer}から作る{deliveryNote}だけ（{project}から開いたとき・{acceptance}は今のまま）
  const og = useOngoing();
  const withPicker = !groupId && !isAc && !!partnerId;
  const [choice, setChoice] = useState<ProjectChoice>("new");
  const groups = useQuery({ queryKey: ["groups", partnerId, "choice"], queryFn: () => api<GroupList>("GET", `/groups?state=all&partner=${partnerId}`), enabled: withPicker });
  const dq = useQuery({ queryKey: ["groups-default", partnerId], queryFn: () => api<GroupDefault>("GET", `/groups/default?partner=${partnerId}`), enabled: withPicker });
  const pq = useQuery({ queryKey: ["partner", partnerId], queryFn: () => api<Partner>("GET", `/partners/${partnerId}`), enabled: withPicker });
  useEffect(() => {
    if (dq.data) setChoice(dq.data.choice);
  }, [dq.data]);
  const choices = (groups.data?.items ?? []).filter((g) => !g.completedAt);
  const ongoingName = pq.data ? ongoingNameOf(displayNameOf(pq.data), og.word) : "";
  const ready = groupId ? !!group.data : !!partnerId && (!withPicker || !!dq.data || dq.isError);
  const create = async () => {
    if (!ready) {
      setErr(t(DELIVERY_NOTE_MSG.customerRequired));
      return;
    }
    setBusy(true);
    setErr(undefined);
    try {
      const s = subject.trim();
      const n = await api<DeliveryNote & CreateWarning>("POST", isAc ? "/acceptances" : "/delivery-notes", { ...(groupId ? { projectGroupId: groupId } : { partnerId, ...(withPicker ? { projectId: choice } : {}) }), ...(s ? { subject: s } : {}) });
      void qc.invalidateQueries({ queryKey: ["delivery-notes"] });
      void qc.invalidateQueries({ queryKey: ["groups-default", partnerId] });
      // 完了した継続案件に作ったときは警告の文（★B＝止めない）
      nav(isAc ? `/acceptances/${n.id}` : `/delivery-notes/${n.id}`, { replace: true, ...(n.warning ? { state: { notice: n.warning } } : {}) });
    } catch (e) {
      // 同じ{project}に下書きがあれば、その下書きを開く
      if (e instanceof ApiError && typeof e.problem.deliveryNoteId === "string") {
        nav(`/delivery-notes/${e.problem.deliveryNoteId}`, { replace: true });
        return;
      }
      setErr(messageOf(e));
      setBusy(false);
    }
  };
  return (
    <>
      <BackButton back={back} />
      <div className="hrow">
        <h1>{pageTitle}</h1>
      </div>
      <div className="paper" style={{ maxWidth: 640, padding: 20 }} data-testid="dn-new-form">
        {groupId ? (
          <p style={{ margin: "0 0 14px" }} data-testid="dn-new-group">
            <span className="muted">{L.project}　</span>
            <b>{group.data?.name ?? "…"}</b>
            <span className="muted">　{group.data?.client.name ?? ""}</span>
          </p>
        ) : (
          <div style={{ marginBottom: 14 }}>
            <PartnerField
              role="customer"
              label={t(DELIVERY_NOTE_MSG.newCustomer)}
              value={shown}
              onPick={(c) => {
                setPartnerId(c.id);
                setChoice("new");
                setPicked({ name: c.displayName, branch: null, contact: null });
                setErr(undefined);
              }}
              testId="dn-new-partner"
            />
          </div>
        )}
        <Field id="dnn_subject" label={`${DELIVERY_NOTE_MSG.newSubject}（任意）`} style={{ marginBottom: 14 }}>
          <input id="dnn_subject" className="inp" placeholder=" " maxLength={200} value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="dn-new-subject" />
        </Field>
        {withPicker ? (
          <div style={{ marginBottom: 14 }}>
            <ProjectPicker groups={choices} value={choice} onChange={(v) => setChoice(v)} ongoingName={ongoingName} subject={subject} loading={!dq.data && !dq.isError} testId="dn-new-project" />
          </div>
        ) : null}
        <ErrorText>{err}</ErrorText>
        <div className="row" style={{ justifyContent: "flex-end" }}>
          <button type="button" className="btn primary" disabled={busy || !ready} onClick={() => void create()} data-testid="dn-new-create">
            {DELIVERY_NOTE_MSG.newSubmit}
          </button>
        </div>
      </div>
    </>
  );
}
